// The aether wipe: Bebop's branded full-screen transition mask, used in
// BOTH directions of the ground↔space handoff (and reusable by any future
// scene swap). The reference trailer masks its scene swaps with ~1 s
// datamosh glitches; Bebop's language is analog and aetheric instead —
// amber static, scan bands, horizontal tears, a breath of vignette — the
// sound of a brass instrument losing and re-finding its signal.
//
// Contract: play() ramps to FULL COVER in COVER_MS, holds, then dissolves.
// The caller swaps scenes only while `covering` is true (game.ts gates
// enterArea on it), and the destination room join runs behind the ramp —
// so the player never sees a load screen, a pop, or an empty frame. Total
// wall-clock stays inside the audit's 0.5–1.5 s window; the console logs
// measured timings for the acceptance record.
//
// DOM/canvas, sized to the viewport, sits above the 3D canvas and below
// the persistent UI layer (chat survives the swap — UI continuity is the
// "one world" storytelling).

// Design total 1080 ms — headroom inside the audit's 0.5–1.5 s window,
// because the cover moment does real work (the surface scene build costs
// a few frames) and the measured total runs ~100–300 ms over design on a
// janky swap. Piece 1a measured 1252 ms (ascent) and 1588 ms (descent)
// on the 450/320/480 envelope; this one keeps the worst case under bar.
const COVER_MS = 420;
const HOLD_MS = 240;
const OUT_MS = 420;
export const WIPE_TOTAL_MS = COVER_MS + HOLD_MS + OUT_MS;

/** Low-res noise field scaled up for chunky analog grain. */
const GRAIN_W = 192;
const GRAIN_H = 108;

export class AetherWipe {
  private root: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D | null;
  private grain: HTMLCanvasElement;
  private grainCtx: CanvasRenderingContext2D | null;
  private startedAt = 0;
  private raf = 0;
  private onFullCover: (() => void) | undefined;
  private coverFired = false;

  /** True from play() until the dissolve finishes. */
  active = false;
  /** True while the screen is fully covered (swap scenes now). */
  covering = false;

  /** Scene swaps should wait for cover — but only while it's coming. */
  get holdSwap(): boolean {
    return this.active && !this.coverFired;
  }

  constructor(parent: HTMLElement) {
    this.root = document.createElement("canvas");
    this.root.className = "aether-wipe";
    this.root.style.display = "none";
    parent.append(this.root);
    this.ctx = this.root.getContext("2d");
    this.grain = document.createElement("canvas");
    this.grain.width = GRAIN_W;
    this.grain.height = GRAIN_H;
    this.grainCtx = this.grain.getContext("2d");
  }

  /** Begin the wipe. `onFullCover` fires once, at first full cover. */
  play(onFullCover?: () => void): void {
    this.onFullCover = onFullCover;
    this.coverFired = false;
    this.startedAt = performance.now();
    this.active = true;
    this.covering = false;
    this.root.width = window.innerWidth;
    this.root.height = window.innerHeight;
    this.root.style.display = "";
    console.log(`[wipe] start at ${this.startedAt.toFixed(0)}ms`);
    cancelAnimationFrame(this.raf);
    const frame = () => {
      if (!this.draw()) {
        this.finish();
        return;
      }
      this.raf = requestAnimationFrame(frame);
    };
    this.raf = requestAnimationFrame(frame);
  }

  private finish(): void {
    this.active = false;
    this.covering = false;
    this.root.style.display = "none";
    console.log(`[wipe] done after ${(performance.now() - this.startedAt).toFixed(0)}ms`);
  }

  /** One frame; returns false when the wipe has run its course. */
  private draw(): boolean {
    const ctx = this.ctx;
    const grainCtx = this.grainCtx;
    if (!ctx || !grainCtx) return false;
    const t = performance.now() - this.startedAt;
    if (t >= WIPE_TOTAL_MS) return false;

    // Opacity envelope: ramp in, hold, dissolve.
    let cover: number;
    if (t < COVER_MS) {
      const f = t / COVER_MS;
      cover = f * f * (3 - 2 * f);
    } else if (t < COVER_MS + HOLD_MS) {
      cover = 1;
    } else {
      const f = (t - COVER_MS - HOLD_MS) / OUT_MS;
      cover = 1 - f * f * (3 - 2 * f);
    }
    const fullyCovered = t >= COVER_MS && t < COVER_MS + HOLD_MS;
    if (fullyCovered && !this.coverFired) {
      this.coverFired = true;
      console.log(`[wipe] full cover at ${t.toFixed(0)}ms`);
      this.onFullCover?.();
    }
    this.covering = fullyCovered;

    const w = this.root.width;
    const h = this.root.height;

    // Analog grain, amber-tinted, denser as cover rises.
    const image = grainCtx.createImageData(GRAIN_W, GRAIN_H);
    const data = image.data;
    for (let i = 0; i < data.length; i += 4) {
      const v = Math.random();
      const bright = v * v * 255;
      data[i] = bright * 0.92; // warm channel balance: brass, not snow
      data[i + 1] = bright * 0.74;
      data[i + 2] = bright * 0.36;
      data[i + 3] = 255;
    }
    grainCtx.putImageData(image, 0, 0);

    ctx.clearRect(0, 0, w, h);
    ctx.globalAlpha = cover;
    ctx.fillStyle = "#070604";
    ctx.fillRect(0, 0, w, h);
    ctx.imageSmoothingEnabled = false;
    ctx.globalAlpha = cover * 0.85;
    ctx.drawImage(this.grain, 0, 0, w, h);

    // Horizontal tears: slices of the grain re-drawn with offset — the
    // signal slipping. More of them near full cover.
    const tears = Math.floor(2 + cover * 6);
    for (let i = 0; i < tears; i++) {
      const y = Math.random() * h;
      const sliceH = 6 + Math.random() * 26;
      const offset = (Math.random() - 0.5) * w * 0.2 * cover;
      const srcY = (y / h) * GRAIN_H;
      ctx.globalAlpha = cover * (0.5 + Math.random() * 0.5);
      ctx.drawImage(
        this.grain,
        0,
        srcY,
        GRAIN_W,
        Math.max(1, (sliceH / h) * GRAIN_H),
        offset,
        y,
        w,
        sliceH,
      );
    }

    // Scan bands: the aether's carrier wave rolling through.
    ctx.globalAlpha = cover * 0.5;
    ctx.fillStyle = "#000000";
    const roll = (t * 0.35) % 8;
    for (let y = -8 + roll; y < h; y += 8) {
      ctx.fillRect(0, y, w, 2.6);
    }

    // A slow amber surge line sweeping down — the one authored shape.
    const surgeY = ((t % 900) / 900) * h;
    const surge = ctx.createLinearGradient(0, surgeY - 60, 0, surgeY + 60);
    surge.addColorStop(0, "rgba(216, 176, 74, 0)");
    surge.addColorStop(0.5, `rgba(216, 176, 74, ${0.35 * cover})`);
    surge.addColorStop(1, "rgba(216, 176, 74, 0)");
    ctx.globalAlpha = 1;
    ctx.fillStyle = surge;
    ctx.fillRect(0, surgeY - 60, w, 120);

    // Vignette so the frame breathes at the edges.
    const vignette = ctx.createRadialGradient(
      w / 2,
      h / 2,
      Math.min(w, h) * 0.3,
      w / 2,
      h / 2,
      Math.max(w, h) * 0.72,
    );
    vignette.addColorStop(0, "rgba(0,0,0,0)");
    vignette.addColorStop(1, `rgba(0,0,0,${0.85 * cover})`);
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, w, h);

    ctx.globalAlpha = 1;
    return true;
  }
}
